import { parseDateValue, pad2, toDateInputString, toTimeString } from "./normalizers";

type OpeningHoursServiceKind = ApiOrderType;
export type OpeningHoursTimeRange = [string, string];

type ServiceFieldMap = Record<OpeningHoursServiceKind, {
    start: keyof CashBoxRestaurantOpeningHours;
    end: keyof CashBoxRestaurantOpeningHours;
}>;

type TimeTemplate = {
    time: string;
    seconds: string;
    offset: string;
};

type OpeningDateRange = {
    startDateTime: Date;
    endDateTime: Date;
    startTime: string;
    endTime: string;
};

export type PickupDateTimeConstraints = {
    initPickupDate: string;
    initPickupTime: string;
    minPickupDate: string;
    maxPickupDate: string;
    minPickupTime: string;
    maxPickupTime: string;
    canChangePickupDate: boolean;
    hasAnyAvailableSlot: boolean;
    openingHoursRestrictionActive: boolean;
    allowedDateValues: string[];
    timeRangesByDate: Record<string, OpeningHoursTimeRange[]>;
};

export type CurrentServiceOpeningStatus = {
    isOpenNow: boolean;
    dateValue: string;
    timeValue: string;
    ranges: OpeningHoursTimeRange[];
    hasConfiguredDay: boolean;
    hasScheduleLoaded: boolean;
    isClosedAllDay: boolean;
    timeRangesText: string;
};

const DAY_NAMES = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
] as const;

const SERVICE_FIELDS: ServiceFieldMap = {
    normal: { start: "start_timezone", end: "end_timezone" },
    pickup: { start: "start_timezone", end: "end_timezone" },
    delivery: { start: "start_delivery_timezone", end: "end_delivery_timezone" },
};

const TIMEZONE_TIME_PATTERN = /T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?([+-]\d{2}:?\d{2}|Z)?$/i;

const buildLocalDate = (dateValue: string, hours = 0, minutes = 0, seconds = 0): Date | null => {
    const [year, month, day] = dateValue.split("-").map(Number);
    if (!year || !month || !day) return null;

    const date = new Date(year, month - 1, day, hours, minutes, seconds, 0);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getDateValueWithOffset = (dateValue: string, dayOffset: number): string => {
    const date = buildLocalDate(dateValue, 12);
    if (!date) return "";
    date.setDate(date.getDate() + dayOffset);
    return toDateInputString(date);
};

const getOpeningHoursEntryForDate = (
    openingHours: CashBoxRestaurantOpeningHours[] | null | undefined,
    dateValue: string
): CashBoxRestaurantOpeningHours | null => {
    if (!openingHours?.length || !dateValue) return null;

    const date = buildLocalDate(dateValue, 12);
    const day = date ? DAY_NAMES[date.getDay()] : "";

    return openingHours.find((entry) => entry.day.toLowerCase() === day) || null;
};

const getTimeTemplate = (value: string): TimeTemplate | null => {
    const match = value.match(TIMEZONE_TIME_PATTERN);
    if (match) {
        const [, hours = "00", minutes = "00", seconds = "00", offset = ""] = match;
        return {
            time: `${pad2(hours)}:${pad2(minutes)}`,
            seconds: pad2(seconds),
            offset,
        };
    }

    const time = toTimeString(value);
    return time ? { time, seconds: "00", offset: "" } : null;
};

const buildDateTimeFromTemplate = (dateValue: string, templateValue: string): Date | null => {
    const template = getTimeTemplate(templateValue);
    if (!dateValue || !template) return null;
    return parseDateValue(`${dateValue}T${template.time}:${template.seconds}${template.offset}`)
        || parseDateValue(`${dateValue}T${template.time}:${template.seconds}`);
};

const getOpeningDateRange = (
    openingHours: CashBoxRestaurantOpeningHours[] | null | undefined,
    dateValue: string,
    serviceKind: OpeningHoursServiceKind
): OpeningDateRange | null => {
    const entry = getOpeningHoursEntryForDate(openingHours, dateValue);
    if (!entry) return null;

    const { start, end } = SERVICE_FIELDS[serviceKind];
    const startDateTime = buildDateTimeFromTemplate(dateValue, String(entry[start] || ""));
    let endDateTime = buildDateTimeFromTemplate(dateValue, String(entry[end] || ""));

    if (!startDateTime || !endDateTime) return null;
    if (endDateTime < startDateTime) {
        endDateTime = buildDateTimeFromTemplate(getDateValueWithOffset(dateValue, 1), String(entry[end] || ""));
    }
    if (!endDateTime || endDateTime < startDateTime) return null;

    return {
        startDateTime,
        endDateTime,
        startTime: toTimeString(startDateTime),
        endTime: toTimeString(endDateTime),
    };
};

const clampRange = (
    range: Pick<OpeningDateRange, "startDateTime" | "endDateTime"> | null,
    minDateTime: Date,
    maxDateTime: Date
): OpeningHoursTimeRange | null => {
    if (!range || range.endDateTime < range.startDateTime) return null;

    const startDateTime = range.startDateTime < minDateTime ? minDateTime : range.startDateTime;
    const endDateTime = range.endDateTime > maxDateTime ? maxDateTime : range.endDateTime;
    if (endDateTime < startDateTime) return null;

    const startTime = toTimeString(startDateTime);
    const endTime = toTimeString(endDateTime);
    return startTime && endTime ? [startTime, endTime] : null;
};

const getDateRangeWindow = (dateValue: string) => ({
    start: buildLocalDate(dateValue, 0, 0, 0),
    end: buildLocalDate(dateValue, 23, 59, 59),
});

export const formatOpeningHoursTimeRanges = (ranges: OpeningHoursTimeRange[]): string => (
    ranges.map(([start, end]) => `${start} - ${end}`).join(", ")
);

export const getOpeningHoursTimeRangesForDate = ({
    openingHours,
    serviceKind,
    dateValue,
    minDateTime,
    maxDateTime,
}: {
    openingHours?: CashBoxRestaurantOpeningHours[] | null;
    serviceKind: OpeningHoursServiceKind;
    dateValue: string;
    minDateTime?: Date;
    maxDateTime?: Date;
}): OpeningHoursTimeRange[] => {
    const window = getDateRangeWindow(dateValue);
    if (!dateValue || !window.start || !window.end) return [];

    const rangeMin = minDateTime && minDateTime > window.start ? minDateTime : window.start;
    const rangeMax = maxDateTime && maxDateTime < window.end ? maxDateTime : window.end;

    const ranges = [
        clampRange(getOpeningDateRange(openingHours, dateValue, serviceKind), rangeMin, rangeMax),
        clampRange(getOpeningDateRange(openingHours, getDateValueWithOffset(dateValue, -1), serviceKind), rangeMin, rangeMax),
    ].filter((range): range is OpeningHoursTimeRange => Boolean(range));

    return ranges.filter(([start, end], index) => (
        ranges.findIndex(([nextStart, nextEnd]) => nextStart === start && nextEnd === end) === index
    )).sort(([first], [second]) => first.localeCompare(second));
};

export const getCurrentServiceOpeningStatus = ({
    openingHours,
    serviceKind,
    referenceDate = new Date(),
}: {
    openingHours?: CashBoxRestaurantOpeningHours[] | null;
    serviceKind: OpeningHoursServiceKind;
    referenceDate?: Date | string | number;
}): CurrentServiceOpeningStatus => {
    if (openingHours == null) {
        return {
            isOpenNow: true,
            dateValue: toDateInputString(referenceDate),
            timeValue: toTimeString(referenceDate),
            ranges: [],
            hasConfiguredDay: true,
            hasScheduleLoaded: false,
            isClosedAllDay: false,
            timeRangesText: "",
        };
    }

    const dateValue = toDateInputString(referenceDate);
    const timeValue = toTimeString(referenceDate);
    const ranges = getOpeningHoursTimeRangesForDate({ openingHours, serviceKind, dateValue });
    const hasConfiguredDay = Boolean(getOpeningHoursEntryForDate(openingHours, dateValue));

    return {
        isOpenNow: ranges.some(([start, end]) => timeValue >= start && timeValue <= end),
        dateValue,
        timeValue,
        ranges,
        hasConfiguredDay,
        hasScheduleLoaded: true,
        isClosedAllDay: !hasConfiguredDay && ranges.length === 0,
        timeRangesText: formatOpeningHoursTimeRanges(ranges),
    };
};

const getNextAvailableOpeningSlot = ({
    openingHours,
    serviceKind,
    minDateTime,
    maxDateTime,
}: {
    openingHours?: CashBoxRestaurantOpeningHours[] | null;
    serviceKind: OpeningHoursServiceKind;
    minDateTime: Date;
    maxDateTime: Date;
}): { dateValue: string; timeValue: string } | null => {
    const cursorDate = buildLocalDate(toDateInputString(minDateTime), 12);
    if (!cursorDate) return null;

    for (let i = 0; i < 32 && cursorDate <= maxDateTime; i += 1) {
        const dateValue = toDateInputString(cursorDate);
        const [firstRange] = getOpeningHoursTimeRangesForDate({
            openingHours,
            serviceKind,
            dateValue,
            minDateTime,
            maxDateTime,
        });

        if (firstRange) {
            return {
                dateValue,
                timeValue: firstRange[0],
            };
        }

        cursorDate.setDate(cursorDate.getDate() + 1);
    }

    return null;
};

export const buildPickupDateTimeConstraints = ({
    pickupDatetime,
    isWaiterCreated,
    preparationTime = 0,
    openingHours,
    serviceKind,
    enforceOpeningHours = false,
}: {
    pickupDatetime?: string;
    isWaiterCreated?: boolean;
    preparationTime?: number;
    openingHours?: CashBoxRestaurantOpeningHours[] | null;
    serviceKind: OpeningHoursServiceKind;
    enforceOpeningHours?: boolean;
}): PickupDateTimeConstraints => {
    const baseDate = parseDateValue(pickupDatetime) || new Date();
    const prepMinutes = Number(preparationTime) || 0;
    const minDateTime = isWaiterCreated
        ? new Date(baseDate.getTime() + (prepMinutes * 60 * 1000))
        : baseDate;
    const maxDateTime = isWaiterCreated
        ? new Date(new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate() + 2).getTime() - 60 * 1000)
        : new Date(baseDate.getTime() + (prepMinutes * 60 * 1000));

    const baseConstraints: PickupDateTimeConstraints = {
        initPickupDate: toDateInputString(minDateTime),
        initPickupTime: toTimeString(minDateTime),
        minPickupDate: toDateInputString(minDateTime),
        maxPickupDate: toDateInputString(maxDateTime),
        minPickupTime: toTimeString(minDateTime),
        maxPickupTime: toTimeString(maxDateTime),
        canChangePickupDate: toDateInputString(minDateTime) !== toDateInputString(maxDateTime),
        hasAnyAvailableSlot: true,
        openingHoursRestrictionActive: false,
        allowedDateValues: [],
        timeRangesByDate: {},
    };

    if (!enforceOpeningHours || openingHours == null) {
        return baseConstraints;
    }

    const timeRangesByDate: Record<string, OpeningHoursTimeRange[]> = {};
    const allowedDateValues: string[] = [];
    const cursorDate = buildLocalDate(baseConstraints.minPickupDate, 12);
    const lastDate = buildLocalDate(baseConstraints.maxPickupDate, 12);

    if (!cursorDate || !lastDate) {
        return baseConstraints;
    }

    while (cursorDate <= lastDate) {
        const dateValue = toDateInputString(cursorDate);
        const ranges = getOpeningHoursTimeRangesForDate({
            openingHours,
            serviceKind,
            dateValue,
            minDateTime,
            maxDateTime,
        });

        if (ranges.length) {
            timeRangesByDate[dateValue] = ranges;
            allowedDateValues.push(dateValue);
        }

        cursorDate.setDate(cursorDate.getDate() + 1);
    }

    if (!allowedDateValues.length) {
        return {
            ...baseConstraints,
            initPickupDate: "",
            initPickupTime: "",
            minPickupDate: "",
            maxPickupDate: "",
            minPickupTime: "",
            maxPickupTime: "",
            canChangePickupDate: false,
            hasAnyAvailableSlot: false,
            openingHoursRestrictionActive: true,
            allowedDateValues,
            timeRangesByDate,
        };
    }

    const nextSlot = getNextAvailableOpeningSlot({
        openingHours,
        serviceKind,
        minDateTime,
        maxDateTime,
    });
    const lastDateValue = allowedDateValues[allowedDateValues.length - 1];

    return {
        ...baseConstraints,
        initPickupDate: nextSlot?.dateValue || baseConstraints.initPickupDate,
        initPickupTime: nextSlot?.timeValue || baseConstraints.initPickupTime,
        minPickupDate: allowedDateValues[0],
        maxPickupDate: lastDateValue,
        minPickupTime: nextSlot?.timeValue || baseConstraints.minPickupTime,
        maxPickupTime: timeRangesByDate[lastDateValue]?.at(-1)?.[1] || baseConstraints.maxPickupTime,
        canChangePickupDate: allowedDateValues.length > 1,
        openingHoursRestrictionActive: true,
        allowedDateValues,
        timeRangesByDate,
    };
};

export const isPickupDateAllowedByConstraints = (
    dateValue: string,
    constraints: PickupDateTimeConstraints
): boolean => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return false;
    if (constraints.openingHoursRestrictionActive) {
        return constraints.allowedDateValues.includes(dateValue);
    }
    if (!constraints.canChangePickupDate) {
        return dateValue === constraints.minPickupDate;
    }
    return dateValue >= constraints.minPickupDate && dateValue <= constraints.maxPickupDate;
};

export const getVisiblePickupTimeRangesForDate = (
    dateValue: string,
    constraints: PickupDateTimeConstraints
): OpeningHoursTimeRange[] => {
    if (!dateValue || !isPickupDateAllowedByConstraints(dateValue, constraints)) return [];
    if (constraints.openingHoursRestrictionActive) {
        return constraints.timeRangesByDate[dateValue] || [];
    }
    if (!constraints.minPickupTime || !constraints.maxPickupTime) return [];
    if (constraints.minPickupDate === constraints.maxPickupDate) {
        return [[constraints.minPickupTime, constraints.maxPickupTime]];
    }
    if (dateValue === constraints.minPickupDate) {
        return [[constraints.minPickupTime, "23:59"]];
    }
    if (dateValue === constraints.maxPickupDate) {
        return [["00:00", constraints.maxPickupTime]];
    }
    return [["00:00", "23:59"]];
};
